import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Mission timer hook for countdown functionality
 * @param {number} totalMinutes - Total time allowed in minutes
 * @param {string} tourSlug - Tour identifier for localStorage
 * @param {boolean} isActive - Whether timer should be running
 */
export function useMissionTimer(totalMinutes, tourSlug, isActive = true) {
  const storageKey = `mission_timer_${tourSlug}`;
  const pauseStorageKey = `${storageKey}_paused`;

  // Initialize from localStorage or start fresh
  const [timeRemaining, setTimeRemaining] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const { remaining, timestamp, paused } = JSON.parse(saved);
        // If paused, don't adjust for elapsed time
        if (paused) {
          return remaining;
        }
        const elapsed = Math.floor((Date.now() - timestamp) / 1000);
        const adjusted = Math.max(0, remaining - elapsed);
        return adjusted;
      }
    } catch (e) {
      // ignore
    }
    return totalMinutes * 60; // Convert minutes to seconds
  });

  const [hasUsedRelief, setHasUsedRelief] = useState(() => {
    try {
      const saved = localStorage.getItem(`${storageKey}_relief_used`);
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });

  const [isPaused, setIsPaused] = useState(() => {
    try {
      const saved = localStorage.getItem(pauseStorageKey);
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });

  const intervalRef = useRef(null);

  // Save state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        remaining: timeRemaining,
        timestamp: Date.now(),
        paused: isPaused
      }));
      localStorage.setItem(pauseStorageKey, isPaused.toString());
    } catch (e) {
      // ignore
    }
  }, [timeRemaining, storageKey, isPaused, pauseStorageKey]);

  // Timer countdown
  useEffect(() => {
    if (!isActive || timeRemaining <= 0 || isPaused) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeRemaining, isPaused]);

  // Format time as HH:MM:SS
  const formatTime = useCallback(() => {
    const hours = Math.floor(timeRemaining / 3600);
    const minutes = Math.floor((timeRemaining % 3600) / 60);
    const seconds = timeRemaining % 60;

    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [timeRemaining]);

  // Get urgency level for color coding
  const getUrgency = useCallback(() => {
    const minutes = timeRemaining / 60;
    if (minutes > 60) return 'safe'; // green
    if (minutes > 30) return 'caution'; // yellow
    if (minutes > 15) return 'warning'; // orange
    return 'critical'; // red
  }, [timeRemaining]);

  // Add relief time (30 minutes)
  const addReliefTime = useCallback(() => {
    if (hasUsedRelief) return false;

    setTimeRemaining(prev => prev + (30 * 60)); // Add 30 minutes
    setHasUsedRelief(true);

    try {
      localStorage.setItem(`${storageKey}_relief_used`, 'true');
    } catch (e) {
      // ignore
    }

    return true;
  }, [hasUsedRelief, storageKey]);

  // Check if eligible for relief (< 30 min and hasn't used)
  const canUseRelief = useCallback(() => {
    const minutes = timeRemaining / 60;
    return !hasUsedRelief && minutes < 30 && minutes > 0;
  }, [hasUsedRelief, timeRemaining]);

  // Pause timer
  const pauseTimer = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Resume timer
  const resumeTimer = useCallback(() => {
    setIsPaused(false);
  }, []);

  // Toggle pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  // Reset timer
  const resetTimer = useCallback(() => {
    setTimeRemaining(totalMinutes * 60);
    setHasUsedRelief(false);
    setIsPaused(false);
    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}_relief_used`);
      localStorage.removeItem(pauseStorageKey);
    } catch (e) {
      // ignore
    }
  }, [totalMinutes, storageKey, pauseStorageKey]);

  // Calculate elapsed seconds
  const totalSeconds = totalMinutes * 60;
  const elapsedSeconds = totalSeconds - timeRemaining;

  return {
    timeRemaining, // seconds remaining
    elapsedSeconds, // seconds elapsed since start
    formattedTime: formatTime(),
    urgency: getUrgency(),
    hasExpired: timeRemaining <= 0,
    canUseRelief: canUseRelief(),
    hasUsedRelief,
    addReliefTime,
    resetTimer,
    isPaused,
    pauseTimer,
    resumeTimer,
    togglePause
  };
}
